import type { CNSConversation } from "@/models/cns";

export interface ConversationTreeNode {
  conversation: CNSConversation;
  children: ConversationTreeNode[];
}

/**
 * Groups project conversations into a parent/child tree using parentConversationPublicId.
 */
export function buildConversationTree(conversations: CNSConversation[]): ConversationTreeNode[] {
  const byId = new Map(conversations.map((c) => [c.id, c]));
  const childrenByParent = new Map<string, CNSConversation[]>();

  for (const conversation of conversations) {
    const parentId = conversation.parentConversationPublicId;
    if (!parentId || !byId.has(parentId)) {
      continue;
    }
    const siblings = childrenByParent.get(parentId) ?? [];
    siblings.push(conversation);
    childrenByParent.set(parentId, siblings);
  }

  const buildNode = (conversation: CNSConversation): ConversationTreeNode => ({
    conversation,
    children: (childrenByParent.get(conversation.id) ?? [])
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      .map(buildNode),
  });

  return conversations
    .filter((c) => !c.parentConversationPublicId || !byId.has(c.parentConversationPublicId))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map(buildNode);
}

export function countDescendantPaths(node: ConversationTreeNode): number {
  if (node.children.length === 0) {
    return 0;
  }
  return node.children.length + node.children.reduce((sum, child) => sum + countDescendantPaths(child), 0);
}
